/**
 * Step 2 — Selezione prodotto: Singolo (base + add-on) o Bundle.
 * Replica il layout dello store StepProdottoSelect, usando dati MSSP.
 */
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { ShoppingBag, Shield, Wifi, Eye, Monitor, Package, ChevronDown, Check } from 'lucide-react';

export default function StepProdottoMSSP({ data, onChange, listinoData, errors }) {
  const [showArrow, setShowArrow] = useState(false);
  const containerRef = useRef(null);

  const checkOverflow = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setShowArrow(
      el.scrollHeight > el.clientHeight + 10 &&
      el.scrollTop + el.clientHeight < el.scrollHeight - 10
    );
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    checkOverflow();
    el.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);
    return () => {
      el.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [checkOverflow]);

  const scrollDown = () => {
    const el = containerRef.current;
    if (el) el.scrollBy({ top: 200, behavior: 'smooth' });
  };

  // Prodotti suddivisi per tipo
  const prodottiPerTipo = useMemo(() => {
    if (!listinoData) return { base: [], awareness: [], ns: [], mssp: [], bundle: [] };
    return {
      base: listinoData.filter(p => p.tipo_prodotto === 'BASE' && p.attivo),
      awareness: listinoData.filter(p => p.tipo_prodotto === 'AWARENESS' && p.attivo),
      ns: listinoData.filter(p => p.tipo_prodotto === 'NETWORK_SURVEILLANCE' && p.attivo),
      mssp: listinoData.filter(p => p.tipo_prodotto === 'MSSP_MONITORING' && p.attivo),
      bundle: listinoData.filter(p => p.tipo_prodotto === 'BUNDLE' && p.attivo),
    };
  }, [listinoData]);

  const prodottoBase = prodottiPerTipo.base[0] || null;
  const prodottoNS = prodottiPerTipo.ns[0] || null;
  const prodottoAwareness = prodottiPerTipo.awareness[0] || null;
  const prodottoMSSP = prodottiPerTipo.mssp[0] || null;
  const bundles = prodottiPerTipo.bundle;

  const mode = data.tipo_selezione || 'singolo';
  const isBaseSelected = !!data.listino_prodotto_base && mode === 'singolo' && prodottoBase &&
    (parseInt(data.listino_prodotto_base) === prodottoBase.id);

  // ── Handlers: Mode toggle ──
  const handleModeChange = (newMode) => {
    onChange({
      ...data,
      tipo_selezione: newMode,
      listino_prodotto_base: null,
      offerta_sorveglianza_rete: '',
      awareness_enabled: false,
      awareness_seats: '',
      servizio_mssp_monitoring: false,
      gettoni_totali: '',
      ore_per_gettone: 2,
    });
  };

  // ── Handlers: Componi ──
  const handleBaseToggle = (checked) => {
    if (checked && prodottoBase) {
      onChange({
        ...data,
        listino_prodotto_base: prodottoBase.id,
        numero_endpoint: Math.max(parseInt(data.numero_endpoint) || 0, prodottoBase.numero_endpoint || 10),
      });
    } else {
      onChange({
        ...data,
        listino_prodotto_base: null,
        numero_endpoint: 10,
        offerta_sorveglianza_rete: '',
        awareness_enabled: false,
        awareness_seats: '',
        servizio_mssp_monitoring: false,
        gettoni_totali: '',
      });
    }
  };

  const handleNSToggle = (checked) => {
    onChange({ ...data, offerta_sorveglianza_rete: checked ? 'FLAT' : '' });
  };

  const handleAwarenessToggle = (checked) => {
    onChange({
      ...data,
      awareness_enabled: checked,
      awareness_seats: checked ? (data.awareness_seats || String(prodottoAwareness?.numero_awareness_seats || 10)) : '',
    });
  };

  const handleMSSPToggle = (checked) => {
    onChange({
      ...data,
      servizio_mssp_monitoring: checked,
      gettoni_totali: checked ? (data.gettoni_totali || String(prodottoMSSP?.numero_gettoni || 20)) : '',
      ore_per_gettone: prodottoMSSP?.ore_per_gettone || 2,
    });
  };

  const handleEndpointChange = (e) => {
    const raw = parseInt(e.target.value, 10) || 0;
    const val = Math.ceil(raw / 10) * 10;
    onChange({ ...data, numero_endpoint: val > 0 ? val : '' });
  };

  // ── Handler: Bundle ──
  const handleBundleSelect = (bundle) => {
    if (data.listino_prodotto_base === bundle.id) {
      onChange({
        ...data,
        listino_prodotto_base: null,
        numero_endpoint: 10,
        offerta_sorveglianza_rete: '',
        awareness_enabled: false,
        awareness_seats: '',
        servizio_mssp_monitoring: false,
        gettoni_totali: '',
      });
      return;
    }

    const inclusi = bundle.prodotti_inclusi_info || [];
    const hasNS = inclusi.some(p => p.tipo_prodotto === 'NETWORK_SURVEILLANCE');
    const hasAWR = inclusi.some(p => p.tipo_prodotto === 'AWARENESS');
    const hasMSSP = inclusi.some(p => p.tipo_prodotto === 'MSSP_MONITORING');

    onChange({
      ...data,
      listino_prodotto_base: bundle.id,
      numero_endpoint: Math.max(parseInt(data.numero_endpoint) || 0, bundle.numero_endpoint || 10),
      offerta_sorveglianza_rete: hasNS ? 'FLAT' : '',
      awareness_enabled: hasAWR,
      awareness_seats: hasAWR ? String(bundle.numero_awareness_seats || 10) : '',
      servizio_mssp_monitoring: hasMSSP,
      gettoni_totali: hasMSSP ? String(bundle.numero_gettoni || 20) : '',
      ore_per_gettone: hasMSSP ? (bundle.ore_per_gettone || 2) : 2,
    });
  };

  const calcolaRisparmio = (bundle) => {
    const inclusi = bundle.prodotti_inclusi_info || [];
    let prezzoAlaCarte = 0;
    inclusi.forEach(p => { prezzoAlaCarte += parseFloat(p.prezzo_base); });
    return (prezzoAlaCarte - parseFloat(bundle.prezzo_base)).toFixed(0);
  };

  const fieldError = (field) => {
    const err = errors?.[`ordine.${field}`] || errors?.[field];
    if (!err) return null;
    const msg = Array.isArray(err) ? err[0] : err;
    return <p className="text-red-500 text-xs mt-1">{msg}</p>;
  };

  const prodottoSelezionato = useMemo(() => {
    if (!data.listino_prodotto_base || !listinoData) return null;
    return listinoData.find(p => p.id === parseInt(data.listino_prodotto_base) || p.id === data.listino_prodotto_base);
  }, [data.listino_prodotto_base, listinoData]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <ShoppingBag className="w-6 h-6 text-yellow-600" />
        <h2 className="text-xl font-bold text-gray-900">Scegli il Prodotto</h2>
      </div>

      {!prodottoBase ? (
        <div className="text-center py-12 text-gray-400">Nessun prodotto disponibile.</div>
      ) : (
        <>
          {/* Toggle Singolo / Bundle */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => handleModeChange('singolo')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                mode === 'singolo'
                  ? 'bg-yellow-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Shield size={16} className="inline mr-2" />
              Prodotti Singoli
            </button>
            {bundles.length > 0 && (
              <button
                type="button"
                onClick={() => handleModeChange('bundle')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  mode === 'bundle'
                    ? 'bg-yellow-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Package size={16} className="inline mr-2" />
                Bundle
              </button>
            )}
          </div>

          <div ref={containerRef} className="flex-1 min-h-0 overflow-y-auto pr-2">
            {mode === 'singolo' ? (
              /* ━━ COMPONI ━━ */
              <div className="space-y-3">
                {/* Base */}
                <div className={`p-4 rounded-lg border ${isBaseSelected ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isBaseSelected}
                      onChange={(e) => handleBaseToggle(e.target.checked)}
                      className="w-4 h-4 mt-0.5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 accent-yellow-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Shield size={16} className="text-yellow-600" />
                        <span className="text-sm font-medium text-gray-800">{prodottoBase.nome_prodotto}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        €{prodottoBase.prezzo_base}/{prodottoBase.numero_endpoint} EP
                        {prodottoBase.scalabile && ` (scalabile +€${prodottoBase.prezzo_scalabilita}/${prodottoBase.incremento_scalabilita} EP)`}
                      </p>
                      {prodottoBase.descrizione_breve && (
                        <p className="text-xs text-gray-400 mt-1">{prodottoBase.descrizione_breve}</p>
                      )}
                    </div>
                  </div>
                  {isBaseSelected && (
                    <div className="mt-3 ml-7">
                      <label className="block text-xs text-gray-500 mb-1">Numero Endpoint *</label>
                      <input
                        type="number"
                        value={data.numero_endpoint || ''}
                        onChange={handleEndpointChange}
                        min={10}
                        step={10}
                        className="w-32 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      />
                      {prodottoBase.numero_endpoint > 0 && (
                        <p className="text-xs text-gray-500 mt-1">Minimo inclusi: {prodottoBase.numero_endpoint}</p>
                      )}
                      {fieldError('numero_endpoint')}
                    </div>
                  )}
                </div>

                {/* NS */}
                {prodottoNS && (
                  <div className={`p-4 rounded-lg border border-gray-200 bg-white ${!isBaseSelected ? 'opacity-40 cursor-not-allowed' : ''}`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={!!data.offerta_sorveglianza_rete}
                        disabled={!isBaseSelected}
                        onChange={(e) => handleNSToggle(e.target.checked)}
                        className="w-4 h-4 mt-0.5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 accent-yellow-600 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Wifi size={16} className="text-blue-500" />
                          <span className="text-sm font-medium text-gray-800">Network Surveillance</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">€{prodottoNS.prezzo_base} flat</p>
                        {prodottoNS.descrizione_breve && <p className="text-xs text-gray-400 mt-1">{prodottoNS.descrizione_breve}</p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Awareness */}
                {prodottoAwareness && (
                  <div className={`p-4 rounded-lg border border-gray-200 bg-white ${!isBaseSelected ? 'opacity-40 cursor-not-allowed' : ''}`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={data.awareness_enabled}
                        disabled={!isBaseSelected}
                        onChange={(e) => handleAwarenessToggle(e.target.checked)}
                        className="w-4 h-4 mt-0.5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 accent-yellow-600 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Eye size={16} className="text-green-500" />
                          <span className="text-sm font-medium text-gray-800">Awareness Training</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          €{prodottoAwareness.prezzo_base}/{prodottoAwareness.numero_awareness_seats || 10} utenze
                          {prodottoAwareness.scalabile && ` (scalabile +€${prodottoAwareness.prezzo_scalabilita}/${prodottoAwareness.incremento_scalabilita} utenze)`}
                        </p>
                      </div>
                    </div>
                    {data.awareness_enabled && (
                      <div className="mt-3 ml-7">
                        <label className="block text-xs text-gray-500 mb-1">Numero Utenze</label>
                        <input
                          type="number"
                          value={data.awareness_seats || ''}
                          onChange={(e) => onChange({ ...data, awareness_seats: e.target.value })}
                          min={1}
                          className="w-32 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                        <span className="text-xs text-gray-500 ml-2">utenze</span>
                      </div>
                    )}
                  </div>
                )}

                {/* MSSP Monitoring */}
                {prodottoMSSP && (
                  <div className={`p-4 rounded-lg border border-gray-200 bg-white ${!isBaseSelected ? 'opacity-40 cursor-not-allowed' : ''}`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={data.servizio_mssp_monitoring || false}
                        disabled={!isBaseSelected}
                        onChange={(e) => handleMSSPToggle(e.target.checked)}
                        className="w-4 h-4 mt-0.5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 accent-yellow-600 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Monitor size={16} className="text-purple-500" />
                          <span className="text-sm font-medium text-gray-800">Monitoraggio Cyber</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          €{prodottoMSSP.prezzo_base} / {prodottoMSSP.numero_gettoni} gettoni × {prodottoMSSP.ore_per_gettone}h
                        </p>
                      </div>
                    </div>
                    {data.servizio_mssp_monitoring && (
                      <div className="mt-3 ml-7 flex items-center gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Gettoni</label>
                          <input
                            type="number"
                            value={data.gettoni_totali || ''}
                            onChange={(e) => onChange({ ...data, gettoni_totali: e.target.value })}
                            min={1}
                            className="w-24 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                            placeholder="20"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Ore/gettone</label>
                          <input
                            type="number"
                            value={prodottoMSSP.ore_per_gettone || 2}
                            readOnly
                            className="w-20 px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-gray-100 cursor-not-allowed"
                          />
                        </div>
                        {parseInt(data.gettoni_totali) > 0 && (
                          <div className="text-xs text-gray-500 mt-4">
                            = {parseInt(data.gettoni_totali) * (prodottoMSSP.ore_per_gettone || 2)}h totali
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* ━━ BUNDLE ━━ */
              <div className="space-y-3">
                {bundles.map((bundle) => {
                  const isSelected = data.listino_prodotto_base === bundle.id;
                  const risparmio = calcolaRisparmio(bundle);
                  const inclusi = bundle.prodotti_inclusi_info || [];

                  return (
                    <div
                      key={bundle.id}
                      onClick={() => handleBundleSelect(bundle)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-yellow-500 bg-yellow-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-yellow-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Package size={18} className={isSelected ? 'text-yellow-600' : 'text-gray-400'} />
                          <span className="font-semibold text-gray-800">{bundle.nome_prodotto}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold text-gray-900">€{bundle.prezzo_base}</span>
                          {parseInt(risparmio) > 0 && (
                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                              Risparmi €{risparmio}
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">{bundle.descrizione?.replace(/MSSP Monitoring/g, 'Supporto Cyber')}</p>
                      <div className="flex gap-2 flex-wrap">
                        {inclusi.map((p) => (
                          <span key={p.id} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md">
                            {p.tipo_prodotto === 'BASE' && <Shield size={12} className="inline mr-1" />}
                            {p.tipo_prodotto === 'NETWORK_SURVEILLANCE' && <Wifi size={12} className="inline mr-1" />}
                            {p.tipo_prodotto === 'AWARENESS' && <Eye size={12} className="inline mr-1" />}
                            {p.tipo_prodotto === 'MSSP_MONITORING' && <Monitor size={12} className="inline mr-1" />}
                            {p.tipo_prodotto === 'MSSP_MONITORING' ? 'Supporto Cyber' : p.nome_prodotto}
                          </span>
                        ))}
                      </div>
                      {bundle.scalabile && (
                        <p className="text-xs text-yellow-600 mt-2">
                          Scalabile: +€{bundle.prezzo_scalabilita}/{bundle.incremento_scalabilita} EP aggiuntivi
                        </p>
                      )}
                      {isSelected && <Check size={20} className="text-yellow-600 mt-1" />}
                    </div>
                  );
                })}

                {/* Numero Endpoint per bundle */}
                {prodottoSelezionato?.tipo_prodotto === 'BUNDLE' && (
                  <div className="p-3 rounded-md border border-gray-200 bg-white">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Shield size={14} className="inline mr-1 text-yellow-600" />
                      Numero Endpoint *
                    </label>
                    <input
                      type="number"
                      value={data.numero_endpoint || ''}
                      onChange={(e) => onChange({ ...data, numero_endpoint: e.target.value })}
                      className="w-32 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      min={10}
                      step={10}
                    />
                    <span className="text-xs text-gray-500 ml-2">
                      (inclusi: {prodottoSelezionato.numero_endpoint || 10}
                      {prodottoSelezionato.scalabile && `, extra: +€${prodottoSelezionato.prezzo_scalabilita}/${prodottoSelezionato.incremento_scalabilita} EP`})
                    </span>
                    {parseInt(data.numero_endpoint) > (prodottoSelezionato.numero_endpoint || 10) && (
                      <p className="text-xs text-orange-600 mt-1">
                        +{parseInt(data.numero_endpoint) - (prodottoSelezionato.numero_endpoint || 10)} endpoint extra
                      </p>
                    )}
                  </div>
                )}

                {/* Extra Awareness per bundle */}
                {prodottoSelezionato?.tipo_prodotto === 'BUNDLE' && data.awareness_enabled && (
                  <div className="p-3 rounded-md border border-gray-200 bg-white">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Utenze Awareness (incluse: {prodottoSelezionato.numero_awareness_seats || 10})
                    </label>
                    <input
                      type="number"
                      value={data.awareness_seats || ''}
                      onChange={(e) => onChange({ ...data, awareness_seats: e.target.value })}
                      className="w-32 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      min={prodottoSelezionato.numero_awareness_seats || 10}
                    />
                  </div>
                )}

                {/* Gettoni MSSP per bundle */}
                {prodottoSelezionato?.tipo_prodotto === 'BUNDLE' && data.servizio_mssp_monitoring && (
                  <div className="p-3 rounded-md border border-gray-200 bg-white">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Monitor size={14} className="inline mr-1 text-purple-500" />
                      Gettoni MSSP (inclusi: {prodottoSelezionato.numero_gettoni || 20})
                    </label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="number"
                        value={data.gettoni_totali || ''}
                        onChange={(e) => onChange({ ...data, gettoni_totali: e.target.value })}
                        className="w-24 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        min={20}
                        step={20}
                      />
                      <div className="text-xs text-gray-500">
                        × {data.ore_per_gettone || 2}h = {(parseInt(data.gettoni_totali) || 0) * (parseInt(data.ore_per_gettone) || 2)}h totali
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {fieldError('listino_prodotto_base')}
          </div>
        </>
      )}

      {showArrow && (
        <div className="flex justify-center mt-2">
          <button
            type="button"
            onClick={scrollDown}
            className="group flex flex-col items-center animate-bounce text-yellow-600 hover:text-yellow-700 transition-colors focus:outline-none"
          >
            <ChevronDown className="w-7 h-7" />
            <span className="text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-500 mt-0.5">
              Scorri per gli altri campi
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
